import { useState, useEffect } from 'react';

/**
 * Legacy `isMobile` threshold: width <= 1024. Kept exactly as it was because
 * every existing screen branches on it.
 */
const TOUCH_MAX = 1024;
/**
 * Where the host shell itself switches to desktop: dev-core-web's layout uses
 * Tailwind `lg:` (min-width 1024) to show the 256px sidebar and swap its
 * padding. A screen whose touch chrome is viewport-fixed (a sticky footer)
 * must stop BELOW that, or the footer runs under the sidebar at exactly
 * 1024 — which is why `isTouchLayout` is `< 1024` and not `<= 1024`.
 */
const DESKTOP_MIN = 1024;
/**
 * Width from which a touch layout may use the denser tablet variant
 * (two-column field grids, two-up card grids). A 768x1024 front-of-house
 * iPad is the device this exists for.
 */
const TABLET_MIN = 700;

export const useResponsive = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= TOUCH_MAX);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            setIsMobile(window.innerWidth <= TOUCH_MAX);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return {
        isMobile,
        isDesktop: !isMobile,
        /**
         * Render the touch layout: below the host shell's own desktop
         * breakpoint, so viewport-fixed chrome never collides with its sidebar.
         */
        isTouchLayout: windowWidth < DESKTOP_MIN,
        /** Touch layout on a tablet-sized viewport (700–1023px): denser than a phone. */
        isTablet: windowWidth >= TABLET_MIN && windowWidth < DESKTOP_MIN,
        /** Touch layout on a phone-sized viewport (< 700px): one column, everything stacked. */
        isPhone: windowWidth < TABLET_MIN,
        windowWidth,
        // Utility functions
        getWidth: (desktop: string, mobile: string) => isMobile ? mobile : desktop,
    };
};
